/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import {
  Breakpoint,
  ICheckboxGroup,
  ICourse,
  SearchHookProps,
  useSearch,
  getSearchQuery,
  useWindowResize,
  LoadingSpinner,
  useQueryPagination,
  useQuerySortOrder,
  useQueryKeyword,
  useQueryFacets,
  ApplicationType,
  ButtonStyleType,
  ButtonSizeType,
  ModalBox
} from "@into-discovery/browsewebshared";
import { deviceDetect } from "@into-discovery/browsewebshared/src/data/device-detect";
import React, { memo, useCallback, useMemo, useRef, useState } from "react";
import SearchResults from "/app/_components/organisms/search/search-results/search-results";
import SearchFiltersMobile from "/app/_components/organisms/search/search-filters-mobile/search-filters-mobile";
import SearchFiltersDesktop from "/app/_components/organisms/search/search-filters-desktop/search-filters-desktop";
import { useSearchParams } from "next/navigation";
import SearchTitle from "/app/_components/atoms/search/search-result-title/search-result-title";
import { ISearchProps } from "/models/search/ISearchProps";
import { mapFacetsToFilters } from "/app/_lib/search/helper/facets";
import { Container, Row } from "react-bootstrap";
import { AxiosResponse } from "axios";
import color from "../../styles/_helpers/branding.module.scss";
import { useTranslations } from "next-intl";
import IButton from "@into-discovery/browsewebshared/src/models/button/IButton";

type Props = {
  pageProps: ISearchProps;
};

const searchApiSettings = {
  baseUrl: `${process.env.NEXT_PUBLIC_SEARCH_SERVICE_URL}/v2`,
  searchTimeOut: parseInt(process.env.NEXT_PUBLIC_SEARCH_TIMEOUT, 10),
  applicationType: ApplicationType.UniBrandedB2c,
  b2cFlag: true
};

function SearchProvider({ pageProps }: Props) {
  const translate = useTranslations(`Redirect`);
  const [isBreakpointLgUp, setIsBreakpointLgUp] = useState(!deviceDetect.isMobile);
  const [isBreakpointMediumUp, setIsBreakpointMediumUp] = useState(!deviceDetect.isMobile);

  useWindowResize((newWidth) => {
    setIsBreakpointMediumUp(newWidth >= Breakpoint.Medium);
    setIsBreakpointLgUp(newWidth >= Breakpoint.Large);
  }, 100);

  const isMoreResultsLoading = useRef<boolean>(false);

  const searchParams = useSearchParams();

  const searchOptions = useMemo(() => {
    const querySortOrder = useQuerySortOrder(searchParams);
    const queryFacets = useQueryFacets(searchParams);
    const queryKeyword = useQueryKeyword(searchParams);
    const queryPagination = useQueryPagination(
      parseInt(process.env.NEXT_PUBLIC_SEARCH_DEFAULT_RESULTS_COUNT),
      parseInt(process.env.NEXT_PUBLIC_SEARCH_MAX_RESULTS_COUNT),
      searchParams
    );

    const result: SearchHookProps & { queryPagination; searchQuery?: string } = {
      keyword: queryKeyword.getKeyword(),
      facets: queryFacets.getMappedFacets(),
      sortOrder: querySortOrder.getSortOrder(),
      page: queryPagination.getCurrentPage(),
      pageSize: pageProps?.defaultPageSize,
      searchApiSettings,
      queryPagination,
      brandId: pageProps.brandId
    };

    result.searchQuery = getSearchQuery(result);

    return result;
  }, [pageProps.brandId, pageProps?.defaultPageSize, searchParams]);

  const loadNextPageHandler = useCallback(() => {
    if (isMoreResultsLoading.current) {
      return;
    }

    isMoreResultsLoading.current = true;

    const nextPageNumber = searchOptions.queryPagination.getCurrentPage() + 1;
    searchOptions.queryPagination.navigateToNextPage(nextPageNumber);
  }, [searchOptions.queryPagination]);

  // This is needed to support "Load more"
  const allCourses = useRef<ICourse[]>([]);

  const searchDataProcessing = useCallback(
    async (data: AxiosResponse<any, any>) => {
      const results = data?.data || [];

      const searchKeyword = decodeURI(results?.searchOptions?.keywords ?? ``);

      const searchQueryId = results?.searchOptions?.searchServiceId;

      let courses: ICourse[];
      let filters: ICheckboxGroup[] = [];

      if (isMoreResultsLoading.current) {
        isMoreResultsLoading.current = false;
        courses = [...allCourses.current, ...results.results];
      } else {
        courses = results.results;
      }

      allCourses.current = courses;

      if (courses.length === pageProps?.maxPageSize) {
        searchOptions.queryPagination.setResultsCount(pageProps?.maxPageSize);
      }

      const allFacets = results?.searchOptions?.facets;

      if (allFacets) {
        const selectedFacets = searchOptions.facets;

        filters = mapFacetsToFilters(allFacets, selectedFacets);
      }

      const totalCourses = results?.searchOptions?.pagingInfo?.total ?? 0;

      const isNextPageAvailable = searchOptions.queryPagination.isNextPageAvailable(totalCourses);

      const processingResult = { searchKeyword, searchQueryId, courses, totalCourses, filters, isNextPageAvailable };

      return processingResult;
    },
    [pageProps?.maxPageSize, searchOptions.facets, searchOptions.queryPagination]
  );

  const {
    data: renderData,
    isLoading: isResultLoading,
    error
  } = useSearch(
    searchOptions.searchQuery,
    searchOptions.searchApiSettings,
    {
      searchKeyword: "",
      searchQueryId: "",
      totalCourses: 0,
      courses: [],
      filters: [],
      isNextPageAvailable: true
    },
    searchDataProcessing,
    [searchOptions]
  );

  // for redirect modal
  const [showModal, setShowModal] = useState(false);
  const [applyURL, setApplyURL] = useState("");
  const [courseName, setCourseName] = useState("");

  const handleApplyClick = (toggleModal: boolean, courseName: string, courseApplyURL: string) => {
    setShowModal(toggleModal);
    setApplyURL(courseApplyURL);
    setCourseName(courseName);
  };

  const modalFooterButton: IButton = {
    buttonStyle: ButtonStyleType.Filled,
    buttonSize: ButtonSizeType.Medium,
    buttonURL: applyURL,
    buttonRole: "link",
    copyColor: color.white,
    backgroundColor: pageProps.invertedTheme ? pageProps.brandPrimaryColor : pageProps.themeColor,
    hoverBackgroundColor: pageProps.invertedTheme ? pageProps.brandPrimaryColor : pageProps.themeColor,
    activeBackgroundColor: pageProps.invertedTheme ? pageProps.brandPrimaryColor : pageProps.themeColor,
    buttonID: "modalbox-apply-cta",
    buttonName: "modalbox-apply-cta",
    buttonAriaLabel: `Apply to ${courseName}`,
    onClick: () => setShowModal(false),
    children: translate(`buttonText`)
  };

  return (
    <>
      <ModalBox
        isOpen={showModal}
        title={translate(`title`)}
        onClose={() => setShowModal(false)}
        footerButton={modalFooterButton}
        autoClickAfterDelay
        modalSize="lg"
        modalVerticallyCenter
      >
        {translate(`message`)}
      </ModalBox>
      <Container className="pt-4">
        {!!error && <p>Error loading data</p>}

        <Row data-insights-index={`${ApplicationType.UniBrandedB2c}-Search`}>
          {isBreakpointLgUp && (
            <div className="col-lg-3 d-md-none d-lg-block">
              <div id="left-sidebar" className="position-relative">
                {!isResultLoading && <SearchFiltersDesktop filters={renderData.filters} />}
              </div>
            </div>
          )}
          <div className="col-lg-9">
            {!isBreakpointLgUp && (
              <div className="col d-lg-none">
                {!isResultLoading && (
                  <SearchFiltersMobile
                    filters={renderData.filters}
                    brandPrimaryColor={pageProps.brandPrimaryColor}
                    themeColor={pageProps.themeColor}
                    invertedTheme={pageProps.invertedTheme}
                  />
                )}
              </div>
            )}
            {isBreakpointLgUp && (
              <div className="row py-2 bg-white">
                <div className="col d-flex justify-content-between align-items-center">
                  <SearchTitle searchTitle={renderData.searchKeyword} countOfResult={renderData.totalCourses} />
                  {/* <SearchSort sortOptions={pageProps?.sortOptions} router={router} searchParams={searchParams} /> */}
                </div>
              </div>
            )}
            {!isBreakpointLgUp && (
              <div className="mb-2">
                <SearchTitle searchTitle={renderData.searchKeyword} countOfResult={renderData.totalCourses} />
              </div>
            )}
            <div className={isResultLoading ? `d-block` : `d-none`}>
              <LoadingSpinner src="/next-assets/loaders/into.svg" />
            </div>
            <SearchResults
              courses={renderData.courses}
              totalCourses={renderData.totalCourses}
              defaultPageSize={pageProps?.defaultPageSize}
              loadNextPage={loadNextPageHandler}
              isDataLoading={isResultLoading && isMoreResultsLoading.current}
              isNextPageAvailable={renderData.isNextPageAvailable}
              searchQueryId={renderData.searchQueryId}
              isBreakpointLgUp={isBreakpointLgUp}
              isBreakpointMediumUp={isBreakpointMediumUp}
              brandCountryCode={pageProps.brandCountryCode}
              brandPrimaryColor={pageProps.brandPrimaryColor}
              themeColor={pageProps.themeColor}
              invertedTheme={pageProps.invertedTheme}
              handleApplyClick={handleApplyClick}
            />
          </div>
        </Row>
      </Container>
    </>
  );
}

export default memo(SearchProvider);
